import { Effect } from 'effect';

import type { Page } from '@vs-shop/domain/pagination';

import type { ProductListingError } from '../capability/productListing/errors';
import type { FacetSet } from '../capability/productListing/facetSet';
import { ProductListing } from '../capability/productListing/ProductListing';
import { scopeKeyOf, type ListingScope } from '../capability/productListing/listingScope';
import type { ListingState } from '../capability/productListing/listingSelection';
import type { ProductSummary } from '../capability/productSchema';
import type { ListingAtoms } from './atom/makeListingAtoms';
import { decodeListingUrl, encodeListingUrl } from './codec';
import type { ListingUrlSyntax } from './url/ListingUrlSyntax';
import type { ListingUrl, QueryPair } from './url/listingUrl';
import { ListingVocabularyError } from './vocabulary/errors';
import type { ListingVocabulary } from './vocabulary/ListingVocabulary';
import { ListingVocabularyProvider } from './vocabulary/ListingVocabularyProvider';

export type ListingJourneyInput = {
  readonly scope: ListingScope;
  readonly url: ListingUrl;
};

export type ListingJourneyDeps<ER> = {
  readonly atoms: Pick<ListingAtoms<ER>, 'listing' | 'dehydrateListing'>;
  readonly syntax: ListingUrlSyntax;
  readonly itemsPerPage: number;
  /** Preserves unfiltered listings when the facet vocabulary is unavailable. */
  readonly fallbackVocabulary?: ListingVocabulary;
  readonly splitQuery: (
    scope: ListingScope,
    query: ReadonlyArray<QueryPair>
  ) => {
    readonly listing: ReadonlyArray<QueryPair>;
    readonly passthrough: ReadonlyArray<QueryPair>;
  };
};

/** A route maps this URL fact onto its own redirect outcome. */
export type ListingRedirect = {
  readonly _tag: 'Redirect';
  readonly url: ListingUrl;
};

export type ListingSeed<A> = {
  readonly _tag: 'Ready';
  readonly scopeKey: ReturnType<typeof scopeKeyOf>;
  readonly state: ListingState;
  readonly firstPageUrl: ListingUrl;
  readonly products: Page<ProductSummary>;
  readonly facets: FacetSet | null;
  readonly dehydrated: ReturnType<ListingAtoms['dehydrateListing']>;
  readonly alongside: A;
};

/** Shares server reads and browser state without owning tenant route outcomes. */
export const makeListingJourney = <ER>(deps: ListingJourneyDeps<ER>) => {
  const prepare = <A, E, R>(
    input: ListingJourneyInput,
    alongside: Effect.Effect<A, E, R>
  ): Effect.Effect<
    ListingSeed<A> | ListingRedirect,
    ProductListingError | ListingVocabularyError | E,
    ProductListing | ListingVocabularyProvider | R
  > =>
    Effect.gen(function* () {
      const { listing, passthrough } = deps.splitQuery(input.scope, input.url.query);
      const url = { pathname: input.url.pathname, query: listing };
      const vocabularyProvider = yield* ListingVocabularyProvider;
      const vocabulary = yield* vocabularyProvider.forScope(input.scope).pipe(
        Effect.catch((error): Effect.Effect<ListingVocabulary, ListingVocabularyError> => {
          if (error instanceof ListingVocabularyError.NotFound) return Effect.fail(error);
          const fallback = deps.fallbackVocabulary;
          if (fallback === undefined) return Effect.fail(error);
          const candidate = decodeListingUrl(url, input.scope, deps.syntax, fallback);
          if (candidate.unknown.length > 0 || candidate.dropped.length > 0) {
            return Effect.fail(error);
          }
          return Effect.logError('Listing vocabulary read failed', error).pipe(Effect.as(fallback));
        })
      );
      const decoded = decodeListingUrl(url, input.scope, deps.syntax, vocabulary);

      if (!decoded.isCanonical) {
        const redirect: ListingRedirect = {
          _tag: 'Redirect',
          url: {
            pathname: decoded.canonicalUrl.pathname,
            query: [...decoded.canonicalUrl.query, ...passthrough],
          },
        };
        return redirect;
      }

      const service = yield* ProductListing;
      const { scope, selection, page } = decoded.state;
      const [products, facets, alongsideResult] = yield* Effect.all(
        [
          service.products({ scope, selection, page, itemsPerPage: deps.itemsPerPage }),
          service
            .facets({ scope, selection })
            .pipe(
              Effect.catch((error) =>
                Effect.logError('Listing facets read failed', error).pipe(Effect.as(null))
              )
            ),
          alongside,
        ],
        { concurrency: 'unbounded' }
      );

      const scopeKey = scopeKeyOf(input.scope);
      const firstPageUrl = encodeListingUrl(
        decoded.base,
        { ...decoded.state, page: 1 },
        deps.syntax,
        vocabulary
      );
      const seed: ListingSeed<A> = {
        _tag: 'Ready',
        scopeKey,
        state: decoded.state,
        firstPageUrl: { ...firstPageUrl, query: [...firstPageUrl.query, ...passthrough] },
        products,
        facets,
        alongside: alongsideResult,
        dehydrated: deps.atoms.dehydrateListing({
          scopeKey,
          base: decoded.base,
          vocabulary,
          state: decoded.state,
          products,
          facets,
        }),
      };
      return seed;
    });

  return { prepare, bind: deps.atoms.listing };
};
